import { Form, ModelSortDirection } from '../types/schema';
import { tInspectionFormInput } from '../types';
import { QueuedReportPayload, ShiftReportPayload } from '../types/reportTypes';
import {
    ITEM_STATUS_CODES,
    LOCAL_STORAGE_KEYS,
    FORM_TYPES,
    DEFAULT_REPORT_LABELS,
    OPERATOR_RESPONSE_TYPES,
} from './constants';
import {
    OPERATOR_RESPONSE_VALUES,
    shouldCreateIssueForPrompt,
} from './formPromptMapping';
import { authClient } from './amplifyClient';
import { toCalendarDateString } from './dateInputUtils';
import { toAwsDateTime } from './awsDateTime';
import {
    listDailyServiceReportByUnitIdAndCreatedAt,
    listInspectionReportByUnitIdAndCreatedAt,
} from '../graphql/queries';

export { toAwsDateTime } from './awsDateTime';

/** Sort-key floor for InspectionReport GSIs (companyId/unitId + createdAt). */
export const INSPECTION_REPORT_CREATED_AT_FLOOR = '2000-01-01T00:00:00.000Z';

const OPERATOR_RESPONSE_VALUE_SET = new Set<string>(Object.values(OPERATOR_RESPONSE_VALUES));

const isReportUMCounterPromptLabel = (label?: string | null): boolean => {
    if (!label) return false;
    if (
        label === DEFAULT_REPORT_LABELS.currentMachineHours ||
        label === DEFAULT_REPORT_LABELS.currentMachineUM
    ) {
        return true;
    }
    return label.toLowerCase().includes('um counter');
};

export { isReportUMCounterPromptLabel };

type UMCounterUnitFields = {
    currentUMCounterValue?: number | null;
    currentMachineHours?: number | null;
    replacedUMCounterValue?: number | null;
};

/** Face-meter reading on the unit (currentUM, then legacy hours). */
export function resolveLastStoredUMCounter(unit?: UMCounterUnitFields | null): number | null {
    if (unit?.currentUMCounterValue != null && Number.isFinite(unit.currentUMCounterValue)) {
        return unit.currentUMCounterValue;
    }
    if (unit?.currentMachineHours != null && Number.isFinite(unit.currentMachineHours)) {
        return unit.currentMachineHours;
    }
    return null;
}

/**
 * Concept: unit UM for scheduling is max(Inspection, Daily Service).
 * Gen2 only stores `currentUMCounterValue`, so writers ratchet to the higher reading
 * instead of last-write-wins (which could drop a higher DS reading when a later
 * inspection reports a lower face value).
 */
/**
 * Face reading to store as Last Service UM when a new reading moves the meter forward.
 * A lower or equal reading leaves the previous service point alone.
 */
export function lastServiceReadingBeforeAdvance(
    previousFace: number | null | undefined,
    newFace: number | null | undefined,
): number | null {
    if (previousFace == null || newFace == null) return null;
    if (!Number.isFinite(previousFace) || !Number.isFinite(newFace)) return null;
    if (newFace <= previousFace) return null;
    return previousFace;
}

export function resolveMaxFiniteUM(
    ...readings: Array<number | null | undefined>
): number | null {
    let max: number | null = null;
    for (const reading of readings) {
        if (reading == null || !Number.isFinite(reading)) continue;
        max = max == null ? reading : Math.max(max, reading);
    }
    return max;
}

/** Running total: face meter + replaced-counter offset (same as equipment effective reading). */
export function resolveLastStoredCombinedUM(unit?: UMCounterUnitFields | null): number | null {
    const face = resolveLastStoredUMCounter(unit);
    if (face == null) return null;
    return face + (unit?.replacedUMCounterValue ?? 0);
}

function parseEnteredUMReading(
    enteredValue: string | number | null | undefined,
): number | null {
    if (enteredValue == null || enteredValue === '') return null;
    const parsed =
        typeof enteredValue === 'number' ? enteredValue : Number.parseFloat(String(enteredValue));
    return Number.isFinite(parsed) ? parsed : null;
}

/** Proposed running total if the operator's face reading is saved (entered + replaced offset). */
export function resolveProposedCombinedUM(
    enteredValue: string | number | null | undefined,
    unit?: UMCounterUnitFields | null,
): number | null {
    const entered = parseEnteredUMReading(enteredValue);
    if (entered == null) return null;
    return entered + (unit?.replacedUMCounterValue ?? 0);
}

/**
 * True when saving this face reading would lower the combined (old + new) running total.
 * A new counter showing 8 after a 14,000 replace is fine once replacedUM is recorded:
 * proposed (8 + 14000) >= previous combined (0 + 14000).
 */
export function wouldCombinedUMDecrease(
    enteredValue: string | number | null | undefined,
    unit?: UMCounterUnitFields | null,
): boolean {
    const previousCombined = resolveLastStoredCombinedUM(unit);
    const proposedCombined = resolveProposedCombinedUM(enteredValue, unit);
    if (previousCombined == null || proposedCombined == null) return false;
    return proposedCombined < previousCombined;
}

/** @deprecated Prefer wouldCombinedUMDecrease — face-only compare false-alarms after counter replace. */
export function isEnteredUMBelowLastStored(
    enteredValue: string | number | null | undefined,
    lastStored: number | null | undefined,
): boolean {
    if (lastStored == null || !Number.isFinite(lastStored)) return false;
    const parsed = parseEnteredUMReading(enteredValue);
    if (parsed == null) return false;
    return parsed < lastStored;
}

/** Non-issue Daily Service answers that should count toward ok (not free-text). */
const isCompletedOkResponse = (prompt: tInspectionFormInput): boolean => {
    const value = prompt.value;
    if (!value) return false;
    if (value === ITEM_STATUS_CODES.ok || value === ITEM_STATUS_CODES.na) return true;
    if (OPERATOR_RESPONSE_VALUE_SET.has(value)) return true;
    if (prompt.subType === OPERATOR_RESPONSE_TYPES.QUANTITY_ONLY) return true;
    return false;
};

// ─── Core Utility Functions ──────────────────────────────────────────────────

/**
 * Iterate a prompt array and count ok/warn/inop values.
 * Daily Service operator responses and auto-flag triggers map into warn/ok
 * so summary counts stay aligned with issue creation.
 */
export function computeSummaryCounts(prompts: tInspectionFormInput[]): {
    okCount: number;
    warnCount: number;
    inopCount: number;
    totalItems: number;
} {
    let okCount = 0;
    let warnCount = 0;
    let inopCount = 0;

    for (const prompt of prompts) {
        if (!prompt.value || isReportUMCounterPromptLabel(prompt.label)) {
            continue;
        }

        if (prompt.value === ITEM_STATUS_CODES.inop) {
            inopCount++;
        } else if (shouldCreateIssueForPrompt(prompt)) {
            warnCount++;
        } else if (isCompletedOkResponse(prompt)) {
            okCount++;
        }
    }

    return { okCount, warnCount, inopCount, totalItems: prompts.length };
}

/**
 * Build a consistent prompt key from title, label, and description.
 */
export function buildPromptKey(prompt: Pick<tInspectionFormInput, 'title' | 'label' | 'description'>): string {
    return `${prompt.title ?? ''}-${prompt.label ?? ''}-${prompt.description ?? ''}`;
}

/**
 * Return true if the value is a status-bearing response (ok, warn, or inop).
 */
export function isStatusBearing(value: string | undefined | null): boolean {
    return (
        value === ITEM_STATUS_CODES.ok ||
        value === ITEM_STATUS_CODES.warn ||
        value === ITEM_STATUS_CODES.inop
    );
}

/**
 * Resolve the form type from a Form object.
 * Uses the formType field if present, otherwise falls back to boolean flags.
 */
export function resolveFormType(form: Pick<Form, 'formType' | 'inspectionForm' | 'dailyServiceLogForm' | 'pmServiceScheduleForm'>): string | null {
    if (form.formType) return form.formType;
    if (form.inspectionForm) return FORM_TYPES.INSPECTION;
    if (form.dailyServiceLogForm) return FORM_TYPES.DAILY_SERVICE;
    if (form.pmServiceScheduleForm) return FORM_TYPES.PM_SERVICE;
    return null;
}

/** Forms listed under the Inspection tab on /forms (includes shift reports). */
export function isInspectionFormsTabForm(
    form: Pick<Form, 'formType' | 'inspectionForm' | 'dailyServiceLogForm' | 'pmServiceScheduleForm'>,
): boolean {
    const formType = resolveFormType(form);
    return (
        formType === FORM_TYPES.INSPECTION ||
        formType === FORM_TYPES.SHIFT_REPORT ||
        formType === null
    );
}

/**
 * Compute the difference in seconds between two ISO timestamps.
 * Returns null if either timestamp is missing or invalid.
 */
export function computeDurationSeconds(start: string | null | undefined, end: string | null | undefined): number | null {
    if (!start || !end) return null;

    const startMs = new Date(start).getTime();
    const endMs = new Date(end).getTime();

    if (isNaN(startMs) || isNaN(endMs)) return null;

    return Math.round((endMs - startMs) / 1000);
}

/** UM reading from queue payload or matching inspection/daily-service prompt. */
export function resolveReportUMCounterValue(
    prompts: tInspectionFormInput[],
    payloadValue?: number | null,
): number | undefined {
    if (payloadValue != null && Number.isFinite(payloadValue)) {
        return payloadValue;
    }

    // Only trust an explicitly labeled counter prompt — never prompts[0]
    // (a leading quantity/item answer must not overwrite the machine meter).
    const counterPrompt = prompts.find((prompt) => isReportUMCounterPromptLabel(prompt.label));
    if (counterPrompt?.value == null || counterPrompt.value === '') {
        return undefined;
    }

    const parsed = Number.parseFloat(String(counterPrompt.value));
    return Number.isFinite(parsed) ? parsed : undefined;
}

type InspectionReportCounterSource = {
    id?: string | null;
    unitName?: string | null;
    umCounterAtInspection?: number | null;
    machineHoursAtInspection?: number | null;
    responseData?: unknown;
    createdAt?: string | null;
    inspectionCompleted?: string | null;
    inspectionStarted?: string | null;
    userName?: string | null;
    warnCount?: number | null;
    inopCount?: number | null;
};

/** Calendar date (YYYY-MM-DD) for display from an inspection report row. */
export function resolveInspectionReportDisplayDate(
    report: Pick<
        InspectionReportCounterSource,
        'inspectionCompleted' | 'createdAt' | 'inspectionStarted'
    > | null | undefined,
): string | null {
    if (!report) return null;
    const raw = report.inspectionCompleted || report.createdAt || report.inspectionStarted;
    return toCalendarDateString(raw);
}

export function parseInspectionReportPrompts(responseData: unknown): tInspectionFormInput[] {
    if (responseData == null) return [];
    if (Array.isArray(responseData)) {
        return responseData as tInspectionFormInput[];
    }
    if (typeof responseData === 'string') {
        try {
            const parsed = JSON.parse(responseData);
            return Array.isArray(parsed) ? (parsed as tInspectionFormInput[]) : [];
        } catch {
            return [];
        }
    }
    return [];
}

/** UM reading persisted on an inspection report row (fields or response JSON). */
export function resolveUMCounterFromInspectionReport(
    report: InspectionReportCounterSource | null | undefined,
): number | undefined {
    if (!report) return undefined;

    if (report.umCounterAtInspection != null && Number.isFinite(report.umCounterAtInspection)) {
        return report.umCounterAtInspection;
    }
    if (report.machineHoursAtInspection != null && Number.isFinite(report.machineHoursAtInspection)) {
        return report.machineHoursAtInspection;
    }

    const prompts = parseInspectionReportPrompts(report.responseData);
    if (prompts.length === 0) return undefined;

    return resolveReportUMCounterValue(prompts);
}

type DailyServiceReportCounterSource = {
    umCounterAtService?: number | null;
    machineHoursAtService?: number | null;
    responseData?: unknown;
};

/** UM reading persisted on a Daily Service report (fields or response JSON). */
export function resolveUMCounterFromDailyServiceReport(
    report: DailyServiceReportCounterSource | null | undefined,
): number | undefined {
    if (!report) return undefined;

    if (report.umCounterAtService != null && Number.isFinite(report.umCounterAtService)) {
        return report.umCounterAtService;
    }
    if (report.machineHoursAtService != null && Number.isFinite(report.machineHoursAtService)) {
        return report.machineHoursAtService;
    }

    const prompts = parseInspectionReportPrompts(report.responseData);
    if (prompts.length === 0) return undefined;

    return resolveReportUMCounterValue(prompts);
}

/**
 * Raise the unit total to a later inspection or Daily Service reading.
 * A reading at or before the unit's last update is left alone so a counter reset stays put.
 */
export function raisedCurrentUM(args: {
    currentUM?: number | null;
    unitUpdatedAt?: string | null;
    readings: Array<{ value?: number | null; at?: string | null }>;
}): number | null {
    const unitTime = args.unitUpdatedAt ? new Date(args.unitUpdatedAt).getTime() : 0;
    const unitTimeKnown = Number.isFinite(unitTime) && unitTime > 0;
    let max: number | null = null;

    for (const reading of args.readings) {
        if (reading.value == null || !Number.isFinite(reading.value)) continue;
        const at = reading.at ? new Date(reading.at).getTime() : 0;
        if (unitTimeKnown && Number.isFinite(at) && at > 0 && at <= unitTime) continue;
        max = max == null ? reading.value : Math.max(max, reading.value);
    }

    if (max == null) return null;
    if (args.currentUM != null && Number.isFinite(args.currentUM) && max <= args.currentUM) return null;
    return max;
}

export async function fetchLatestInspectionReportForUnit(
    unitId: string,
): Promise<InspectionReportCounterSource | null> {
    const response = (await authClient.graphql({
        query: listInspectionReportByUnitIdAndCreatedAt,
        variables: {
            unitId,
            limit: 1,
            sortDirection: ModelSortDirection.DESC,
            createdAt: { ge: INSPECTION_REPORT_CREATED_AT_FLOOR },
        },
    })) as {
        data?: {
            listInspectionReportByUnitIdAndCreatedAt?: {
                items?: InspectionReportCounterSource[];
            };
        };
    };

    return response.data?.listInspectionReportByUnitIdAndCreatedAt?.items?.[0] ?? null;
}

export async function fetchLatestDailyServiceReportForUnit(unitId: string) {
    const response = (await authClient.graphql({
        query: listDailyServiceReportByUnitIdAndCreatedAt,
        variables: {
            unitId,
            limit: 1,
            sortDirection: ModelSortDirection.DESC,
            createdAt: { ge: INSPECTION_REPORT_CREATED_AT_FLOOR },
        },
    })) as {
        data?: {
            listDailyServiceReportByUnitIdAndCreatedAt?: {
                items?: Array<{
                    createdAt?: string | null;
                    inspectionCompleted?: string | null;
                    userName?: string | null;
                    warnCount?: number | null;
                    inopCount?: number | null;
                    umCounterAtService?: number | null;
                    machineHoursAtService?: number | null;
                    responseData?: unknown;
                }>;
            };
        };
    };

    return response.data?.listDailyServiceReportByUnitIdAndCreatedAt?.items?.[0] ?? null;
}

export function resolveDailyServiceReportDisplayDate(report: {
    inspectionCompleted?: string | null;
    createdAt?: string | null;
} | null | undefined): string | null {
    if (!report) return null;
    const raw = report.inspectionCompleted || report.createdAt;
    return toCalendarDateString(raw);
}

export function resolveReportUnitId(
    item: { unitId?: string | null },
    createdReport?: { unitId?: string | null },
): string | undefined {
    const fromStorage =
        typeof localStorage !== 'undefined'
            ? localStorage.getItem(LOCAL_STORAGE_KEYS.UNIT_ID) || undefined
            : undefined;
    return item.unitId || createdReport?.unitId || fromStorage || undefined;
}

// ─── Queue Management Functions ──────────────────────────────────────────────

/**
 * Append a report payload to the REPORT_QUEUE in localStorage.
 * Handles corrupted JSON by resetting to an empty array.
 */
export function appendToReportQueue(payload: QueuedReportPayload): void {
    let queue: QueuedReportPayload[] = [];
    try {
        const raw = localStorage.getItem(LOCAL_STORAGE_KEYS.REPORT_QUEUE);
        if (raw) {
            queue = JSON.parse(raw);
            if (!Array.isArray(queue)) {
                queue = [];
            }
        }
    } catch (e) {
        console.error('Corrupted REPORT_QUEUE in localStorage, resetting to empty array.', e);
        queue = [];
    }

    queue.push(payload);
    localStorage.setItem(LOCAL_STORAGE_KEYS.REPORT_QUEUE, JSON.stringify(queue));
}

/**
 * Read and parse the REPORT_QUEUE array from localStorage.
 * Handles corrupted JSON by resetting to an empty array.
 */
export function getReportQueue(): QueuedReportPayload[] {
    try {
        const raw = localStorage.getItem(LOCAL_STORAGE_KEYS.REPORT_QUEUE);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) {
            console.error('REPORT_QUEUE is not an array, resetting to empty array.');
            localStorage.setItem(LOCAL_STORAGE_KEYS.REPORT_QUEUE, JSON.stringify([]));
            return [];
        }
        return parsed;
    } catch (e) {
        console.error('Corrupted REPORT_QUEUE in localStorage, resetting to empty array.', e);
        localStorage.setItem(LOCAL_STORAGE_KEYS.REPORT_QUEUE, JSON.stringify([]));
        return [];
    }
}

/**
 * Remove the first item from the REPORT_QUEUE and write back.
 * Handles corrupted JSON by resetting to an empty array.
 */
export function shiftReportQueue(): void {
    let queue: QueuedReportPayload[] = [];
    try {
        const raw = localStorage.getItem(LOCAL_STORAGE_KEYS.REPORT_QUEUE);
        if (raw) {
            queue = JSON.parse(raw);
            if (!Array.isArray(queue)) {
                queue = [];
            }
        }
    } catch (e) {
        console.error('Corrupted REPORT_QUEUE in localStorage, resetting to empty array.', e);
        queue = [];
    }

    queue.shift();
    localStorage.setItem(LOCAL_STORAGE_KEYS.REPORT_QUEUE, JSON.stringify(queue));
}

export type ReportSentSummary = {
    report: {
        id: string;
        source: string;
        slugLabel: string;
    };
    equipmentName: string | null;
    totalCount: number;
    completedAt: string;
};

export function persistReportSentSummary(summary: ReportSentSummary): void {
    try {
        sessionStorage.setItem(LOCAL_STORAGE_KEYS.REPORT_SENT_SUMMARY, JSON.stringify(summary));
    } catch {
        /* ignore quota / private mode */
    }
}

export function readReportSentSummary(): ReportSentSummary | null {
    try {
        const raw = sessionStorage.getItem(LOCAL_STORAGE_KEYS.REPORT_SENT_SUMMARY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as ReportSentSummary;
        if (!parsed?.report?.id || !parsed.report.source) return null;
        return parsed;
    } catch {
        return null;
    }
}

export function clearReportSentSummary(): void {
    try {
        sessionStorage.removeItem(LOCAL_STORAGE_KEYS.REPORT_SENT_SUMMARY);
    } catch {
        /* ignore */
    }
}

/** GraphQL input for createShiftReport — matches amplify/data ShiftReport model. */
export type CreateShiftReportMutationInput = {
    companyId: string;
    userId: string;
    formId?: string | null;
    shiftType?: string | null;
    responseData?: string | null;
    userName?: string | null;
    locationId?: string | null;
    locationName?: string | null;
    alwaysTriggerEmail?: boolean | null;
    okCount?: number | null;
    warnCount?: number | null;
    inopCount?: number | null;
    totalItems?: number | null;
    inspectionDurationSeconds?: number | null;
    signature?: string | null;
    photos?: Array<string | null> | null;
    gpsLatitude?: number | null;
    gpsLongitude?: number | null;
    gpsLocationType?: string | null;
    inspectionStarted?: string | null;
    inspectionCompleted?: string | null;
    createdAt?: string | null;
};

export function resolveShiftReportLocationName(
    responseData: string,
    locationName?: string | null,
): string | undefined {
    if (locationName) {
        return locationName;
    }

    try {
        const parsedResponses = JSON.parse(responseData) as Array<{ subType?: string; label?: string; value?: string }>;
        const locationPrompt = parsedResponses.find(
            (prompt) => prompt.subType === 'dropdown' && prompt.label?.toLowerCase().includes('location'),
        );
        return locationPrompt?.value || undefined;
    } catch {
        return undefined;
    }
}

export function buildCreateShiftReportInput(params: {
    item: ShiftReportPayload;
    promptArray: tInspectionFormInput[];
    signaturePath: string | null;
    userId: string;
    userName: string;
    inspectionCompleted?: string;
}): CreateShiftReportMutationInput {
    const inspectionCompleted = params.inspectionCompleted ?? new Date().toISOString();
    const inspectionStarted = toAwsDateTime(params.item.inspectionStarted, inspectionCompleted);
    const inspectionDurationSeconds = computeDurationSeconds(inspectionStarted, inspectionCompleted);

    return {
        companyId: params.item.companyId,
        userId: params.userId,
        formId: params.item.formId,
        responseData: JSON.stringify(params.promptArray),
        okCount: params.item.okCount,
        warnCount: params.item.warnCount,
        inopCount: params.item.inopCount,
        totalItems: params.item.totalItems,
        inspectionDurationSeconds,
        signature: params.signaturePath,
        photos: params.promptArray.flatMap((prompt) => prompt.photos || []).filter(Boolean),
        gpsLatitude: params.item.gpsLatitude,
        gpsLongitude: params.item.gpsLongitude,
        gpsLocationType: params.item.gpsLocationType,
        inspectionStarted,
        inspectionCompleted,
        createdAt: inspectionCompleted,
        shiftType: params.item.shiftType,
        userName: params.userName,
        locationId: params.item.locationId,
        alwaysTriggerEmail: params.item.alwaysTriggerEmail,
        locationName: resolveShiftReportLocationName(params.item.responseData, params.item.locationName),
    };
}

/** Backends deployed before ShiftReport gained locationName / alwaysTriggerEmail reject those fields. */
export function toLegacyCreateShiftReportInput(
    input: CreateShiftReportMutationInput,
): CreateShiftReportMutationInput {
    const { locationName: _locationName, alwaysTriggerEmail: _alwaysTriggerEmail, ...legacyInput } = input;
    return legacyInput;
}

export function isCreateShiftReportInputFieldError(error: unknown): boolean {
    const matchesFieldError = (message?: string | null) =>
        Boolean(message?.includes('CreateShiftReportInput') && message?.includes('not defined'));

    if (typeof error === 'object' && error !== null) {
        const graphQLError = error as {
            message?: string;
            errors?: Array<{ message?: string | null }>;
        };

        if (matchesFieldError(graphQLError.message)) {
            return true;
        }

        if (Array.isArray(graphQLError.errors)) {
            return graphQLError.errors.some((entry) => matchesFieldError(entry.message));
        }
    }

    const serialized = typeof error === 'string' ? error : JSON.stringify(error ?? {});
    return matchesFieldError(serialized);
}

/** Backends deployed before DailyServiceReport gained alwaysTriggerEmail reject that field. */
export function toLegacyCreateDailyServiceReportInput(
    input: Record<string, unknown>,
): Record<string, unknown> {
    const { alwaysTriggerEmail: _alwaysTriggerEmail, ...legacyInput } = input;
    return legacyInput;
}

export function isCreateDailyServiceReportInputFieldError(error: unknown): boolean {
    const matchesFieldError = (message?: string | null) =>
        Boolean(message?.includes('CreateDailyServiceReportInput') && message?.includes('not defined'));

    if (typeof error === 'object' && error !== null) {
        const graphQLError = error as {
            message?: string;
            errors?: Array<{ message?: string | null }>;
        };

        if (matchesFieldError(graphQLError.message)) {
            return true;
        }

        if (Array.isArray(graphQLError.errors)) {
            return graphQLError.errors.some((entry) => matchesFieldError(entry.message));
        }
    }

    const serialized = typeof error === 'string' ? error : JSON.stringify(error ?? {});
    return matchesFieldError(serialized);
}
